import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, User, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ASSETS } from "@/constants/assets";
import Footer from "@/components/Footer";
import PasswordInput from "@/components/PasswordInput";

const COUNTRIES = [
  "République Démocratique du Congo", "Cameroun", "Canada", "France", "Belgique",
  "Sénégal", "Côte d'Ivoire", "Bénin", "Togo", "Gabon", "Congo-Brazzaville",
  "Mali", "Burkina Faso", "Guinée", "Niger", "Tchad", "Rwanda", "Burundi",
  "Maroc", "Tunisie", "Algérie", "Suisse", "Luxembourg", "Haïti", "États-Unis", "Autre",
];

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: "", email: "", whatsapp: "", country: "", city: "",
    password: "", confirm: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
    localStorage.removeItem("ph_prospect_id");
    localStorage.removeItem("ph_prospect_email");
  }, [user, navigate]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (form.password.length < 6) return toast.error("Le mot de passe doit contenir au moins 6 caractères.");
    if (form.password !== form.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        whatsapp: form.whatsapp.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        password: form.password,
      });
      localStorage.setItem("ph_token", data.token);
      setUser(data.user);
      toast.success(`Bienvenue ${data.user.full_name.split(" ")[0]} — votre espace membre est prêt.`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FB]" data-testid="register-page">
      <div className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-lg"
        >
          {/* Logo header */}
          <div className="flex flex-col items-center mb-8">
            <div className="rounded-full bg-white shadow-md border border-[#E4E7EC] p-2">
              <img src={ASSETS.logo} alt="Institut PROSPECT'HEURE" className="h-16 w-16 md:h-20 md:w-20 rounded-full object-contain" />
            </div>
            <div className="text-[11px] text-[#9A762F] tracking-[0.22em] uppercase font-semibold mt-4">
              Institut PROSPECT&rsquo;HEURE
            </div>
          </div>

          <form
            onSubmit={submit}
            className="ph-card p-6 md:p-8 rounded-2xl border border-[#E4E7EC] shadow-sm"
            data-testid="register-form"
            autoComplete="off"
          >
            <Link to="/" className="text-[11px] text-[#667085] tracking-[0.22em] uppercase font-semibold hover:text-[#9A762F]">
              ← Retour
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0B1726] mt-4 tracking-tight">
              Créer votre compte
            </h1>
            <p className="text-[14px] text-[#475467] mt-2">
              Espace membre officiel de l&rsquo;Institut PROSPECT&rsquo;HEURE.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4">
              <Field icon={User} label="Nom complet" testid="register-fullname"
                value={form.full_name} onChange={set("full_name")} placeholder="Prénom Nom" required />
              <Field icon={Mail} label="Adresse e-mail" type="email" testid="register-email"
                value={form.email} onChange={set("email")} placeholder="vous@email.com" required autoComplete="new-email" />
              <Field icon={Phone} label="Numéro WhatsApp" testid="register-whatsapp"
                value={form.whatsapp} onChange={set("whatsapp")} placeholder="+243 900 000 000" required />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-[#475467] tracking-wider uppercase font-semibold">Pays</label>
                  <select
                    required value={form.country} onChange={set("country")}
                    className="ph-input w-full px-3 py-2.5 rounded-md text-[14px] mt-1"
                    data-testid="register-country"
                  >
                    <option value="">Sélectionnez…</option>
                    {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <Field icon={MapPin} label="Ville" testid="register-city"
                  value={form.city} onChange={set("city")} placeholder="Kinshasa" required />
              </div>

              <PasswordInput label="Mot de passe" testid="register-password"
                value={form.password} onChange={set("password")}
                placeholder="Minimum 6 caractères" required minLength={6} autoComplete="new-password" />
              <PasswordInput label="Confirmer le mot de passe" testid="register-confirm"
                value={form.confirm} onChange={set("confirm")}
                placeholder="Retapez le mot de passe" required minLength={6} autoComplete="new-password" />

              <button
                type="submit" disabled={loading}
                className="gold-btn gold-btn-pill w-full inline-flex items-center justify-center gap-2 py-3 mt-2 disabled:opacity-60"
                data-testid="register-submit"
              >
                {loading ? "Création…" : "Créer mon espace membre"} <ArrowRight size={16} />
              </button>

              <div className="text-[13px] text-[#667085] pt-3 text-center border-t border-[#E4E7EC]">
                Vous avez déjà un compte ?{" "}
                <Link to="/login" className="text-[#9A762F] font-semibold hover:underline" data-testid="register-to-login">
                  Se connecter
                </Link>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[#475467] tracking-wider uppercase font-semibold">{label}</span>
      <div className="relative mt-1">
        {Icon && <Icon size={16} className="absolute left-3 top-3 text-[#98A2B3]" />}
        <input
          {...rest}
          className={`ph-input w-full ${Icon ? "pl-10" : "pl-3"} pr-3 py-2.5 rounded-md text-[14px]`}
          data-testid={testid}
        />
      </div>
    </label>
  );
}
